// Sezione Listini — l'agente vede i listini assegnati, scarica il PDF (IT/EN) o lo invia ai clienti via mail.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Mail, Tags, Loader2 } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import SendDocEmailDialog from "../../components/SendDocEmailDialog";
import { API } from "./_shared";

const isoWeek = () => {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7), year: d.getUTCFullYear() };
};

export default function AgentPricelists() {
  const [pricelists, setPricelists] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [mailTarget, setMailTarget] = useState(null);
  const { week, year } = isoWeek();

  useEffect(() => {
    axios.get(`${API}/agent/my-pricelists`).then((r) => setPricelists(r.data || [])).catch(() => setPricelists([]));
  }, []);

  const downloadPdf = async (pl, lang) => {
    const key = `${pl.id}-${lang}`;
    setDownloading(key);
    try {
      const r = await axios.get(`${API}/agent/pricelists/${pl.id}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Listino_${pl.name.replace(/[^A-Za-z0-9_-]/g, "_")}_W${week}_${year}_${lang.toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`PDF listino ${pl.name} scaricato`);
    } catch (e) {
      toast.error("Errore nel download del PDF");
    } finally { setDownloading(""); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="agent-pricelists-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Listini</h1>
        <p className="text-sm text-stone-500 mt-1">I listini assegnati a te — scaricali in PDF o inviali ai tuoi clienti dalla tua casella email. Settimana {week}/{year}.</p>
      </div>

      {pricelists === null && (
        <div className="flex items-center gap-2 text-stone-500 text-sm p-6"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      )}
      {pricelists?.length === 0 && (
        <Card className="p-8 text-center text-stone-500 bg-white border-stone-200" data-testid="agent-pricelists-empty">
          Nessun listino assegnato: contatta l'amministrazione.
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {pricelists?.map((pl) => (
          <Card key={pl.id} className="p-5 bg-white border-stone-200 flex flex-col gap-4" data-testid={`agent-pricelist-card-${pl.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8A1C2A]/10 flex items-center justify-center shrink-0">
                <Tags className="w-5 h-5 text-[#8A1C2A]" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-stone-900 truncate">{pl.name}</p>
                <p className="text-xs text-stone-500">PDF settimana {week}/{year}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-auto">
              <Button size="sm" variant="outline" onClick={() => downloadPdf(pl, "it")} disabled={downloading === `${pl.id}-it`} className="border-stone-300" data-testid={`pl-pdf-it-${pl.id}`}>
                {downloading === `${pl.id}-it` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} PDF IT
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPdf(pl, "en")} disabled={downloading === `${pl.id}-en`} className="border-stone-300" data-testid={`pl-pdf-en-${pl.id}`}>
                {downloading === `${pl.id}-en` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} PDF EN
              </Button>
              <Button size="sm" onClick={() => setMailTarget(pl)} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white ml-auto" data-testid={`pl-send-mail-${pl.id}`}>
                <Mail className="w-3.5 h-3.5 mr-1" /> Invia via email
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <SendDocEmailDialog
        open={!!mailTarget}
        onClose={() => setMailTarget(null)}
        title={`Invia listino "${mailTarget?.name || ""}"`}
        attachmentLabel={mailTarget ? `Listino ${mailTarget.name} — Settimana ${week}/${year} (PDF)` : ""}
        endpoint={mailTarget ? `${API}/agent/pricelists/${mailTarget.id}/send-email` : ""}
        subjectDefault={mailTarget ? `Listino ${mailTarget.name} — Giotto Carni (Settimana ${week}/${year})` : ""}
        bodyDefault={`Gentile cliente,\n\nin allegato il nostro listino aggiornato della settimana ${week}/${year}.\nRestiamo a disposizione per ordini e informazioni.\n\nCordiali saluti`}
        langSelector
      />
    </div>
  );
}
